// src/pages/warehouse/NullmengenPage.tsx
// NULL-MENGEN (Plan 2026-10-27, 0172): Leiter-Übersicht der Grundartikel ohne Palette im Großlager.
// Nur Anzeige: gezählt wird in der Scan-App, DR-Menge ist reine Information (kein Schreibpfad).
// Statistik, Filter, Sortierung und Verlauf kommen aus GET /warehouse/nullmengen/uebersicht/ und /journal/.
// Freigabe je Mitarbeiter und Modul-Schalter liegen auf der Mitarbeiter-Seite.
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import {
  useModuleSettings, useNullmengenJournal, useNullmengenUebersicht,
  type NullmengenFall, type NullmengenJournalEintrag, type NullmengenMitarbeiter, type NullmengenPackort,
  type NullmengenPhase, type NullmengenStatistik, type NullmengenZaehlPhase, type NullmengenZaehlstand, type NullmengenZeile,
} from '@/api/warehouse'
import { GrundartikelLink } from '@/components/catalog/GrundartikelLink'
import { warehouseErrorMessage } from '@/lib/warehouse-company'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

export const PACKORT_TEXT: Record<NullmengenFall['packort'], string> = {
  packstation: 'Packstation', packstation_ungezaehlt: 'Packstation (ungezählt)',
  packplatz: 'Packplatz', packplatz_ungezaehlt: 'Packplatz (ungezählt)', kein: 'Kein Packort',
}
const PACKORTE: NullmengenPackort[] = ['packstation', 'packstation_ungezaehlt', 'packplatz', 'packplatz_ungezaehlt', 'kein']
export function phaseText(f: Pick<NullmengenFall, 'phase' | 'runde' | 'faellig_ab'>): string {
  if (f.phase === 'stufe1') return 'Stufe 1 (Wochenabsatz)'
  if (f.phase === 'stufe2') return f.faellig_ab === null ? 'Stufe 2 scharf' : 'Stufe 2 (Set-Menge)'
  if (f.phase === 'leer_warten') return 'Wartet auf Bestand 0'
  if (f.phase === 'nachlauf') return `Nachlauf ${f.runde}/3`
  return 'Geschlossen'
}
/** Kurzform ohne Fälligkeit: für Verteilung, Verlauf und das Ergebnis einer Zählung. */
function phaseKurz(phase: NullmengenPhase, runde: number): string {
  if (phase === 'stufe1') return 'Stufe 1'
  if (phase === 'stufe2') return 'Stufe 2'
  if (phase === 'leer_warten') return 'Wartet auf Bestand 0'
  if (phase === 'nachlauf') return `Nachlauf ${runde}/3`
  return 'Geschlossen'
}
export function ergebnisText(e: Pick<NullmengenJournalEintrag, 'ergebnis_phase' | 'ergebnis_runde'>): string {
  return phaseKurz(e.ergebnis_phase, e.ergebnis_runde)
}
const PHASE_OPTION: Record<NullmengenPhase, string> = {
  stufe1: 'Stufe 1', stufe2: 'Stufe 2', leer_warten: 'Wartet auf Bestand 0', nachlauf: 'Nachlauf', geschlossen: 'Geschlossen',
}
const ZAEHL_PHASEN: NullmengenZaehlPhase[] = ['stufe1', 'stufe2', 'nachlauf']
const SCHLIESSGRUND: Record<string, string> = {
  ausgeschlossen: 'Ausgeschlossen (gel\u00f6scht/unbewegt)',
  nachlauf_fertig: 'Nachlauf fertig (leer)', aufgefuellt: 'Aufgefüllt', grosslager_belegt: 'Großlager wieder belegt',
}
const STATUS_TEXT = {
  faellig: 'Heute zu zählen', ueberfaellig: 'Über Frist', nicht_faellig: 'Nicht fällig', gezaehlt: 'Schon gezählt (offen)',
  nie_gezaehlt: 'Nie gezählt (offen)', heute_gezaehlt: 'Heute gezählt', klaerfall: 'Klärfälle', geloescht: 'GELÖSCHT in DR',
  geschlossen: 'Geschlossen', alle: 'Alle (offen und geschlossen)',
} as const
type Status = '' | keyof typeof STATUS_TEXT
const istStatus = (v: string): v is Status => v === '' || Object.prototype.hasOwnProperty.call(STATUS_TEXT, v)
const ZAEHLSTAND: Record<NullmengenZaehlstand, { text: string; titel: string; ton: string }> = {
  nicht_gezaehlt: { text: 'Nicht gezählt', titel: 'Jetzt fällig, in der Scan-App zählen', ton: 'border-amber-500 bg-amber-50 text-amber-800' },
  gezaehlt: { text: 'Gezählt', titel: 'Gezählt, nächste Runde noch nicht fällig', ton: 'border-emerald-600 bg-emerald-50 text-emerald-800' },
  wartet: { text: 'Wartet, nie gezählt', titel: 'Noch nicht fällig und noch nie gezählt', ton: 'border-line bg-panel-2 text-ink-2' },
  klaerfall: { text: 'Klärfall', titel: 'Erst den Packort klären, dann zählen', ton: 'border-red-600 bg-red-50 text-red-800' },
  geschlossen: { text: 'Geschlossen', titel: 'Fall ist abgeschlossen', ton: 'border-line bg-panel-2 text-ink-2' },
}

const datum = (iso: string | null) => iso === null ? '—'
  : new Date(iso).toLocaleDateString('de-DE', { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: '2-digit' })
const datumZeit = (iso: string | null) => iso === null ? '—'
  : new Date(iso).toLocaleString('de-DE', { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
const WOCHENTAG = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa']
/** „2026-10-28“ → „Mi 28.10.“ (Kalendertag, keine Zeitzonen-Umrechnung). */
function tagKurz(tag: string): string {
  const [j, m, t] = tag.split('-').map(Number)
  return `${WOCHENTAG[new Date(Date.UTC(j, m - 1, t)).getUTCDay()]} ${String(t).padStart(2, '0')}.${String(m).padStart(2, '0')}.`
}
const zaehlungenText = (n: number) => n === 1 ? '1 Zählung' : `${n} Zählungen`

/** DR-Abweichung: Plattform-Bestand gegen DR-Menge, nur zur Anzeige. */
export function drAbweichung(f: Pick<NullmengenFall, 'bestand' | 'dr_menge'>): number | null {
  return f.bestand === null || f.dr_menge === null ? null : f.dr_menge - f.bestand
}

/** Freitext erst nach einer kurzen Tipp-Pause abfragen (wie im Journal). */
function useDebounced<T>(value: T, ms = 400): T {
  const [v, setV] = useState(value)
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t) }, [value, ms])
  return v
}
/** <input type="date"> meldet beim Tippen Zwischenstände — nur ein vollständiges Datum geht an den Server. */
const fertigesDatum = (v: string) => /^20\d\d-\d\d-\d\d$/.test(v) ? v : ''

type FallFilter = {
  status: Status; phase: string; packort: string; worker_id: string; q: string
  gezaehlt_von: string; gezaehlt_bis: string; faellig_von: string; faellig_bis: string
  faellig_arbeitstag: string
  geaendert_von: string; geaendert_bis: string; tage: string
}
const LEER: FallFilter = { status: '', phase: '', packort: '', worker_id: '', q: '', gezaehlt_von: '', gezaehlt_bis: '',
  faellig_von: '', faellig_bis: '', faellig_arbeitstag: '', geaendert_von: '', geaendert_bis: '', tage: '30' }
const DATUMSFELDER = ['gezaehlt_von', 'gezaehlt_bis', 'faellig_von', 'faellig_bis', 'geaendert_von', 'geaendert_bis'] as const
type SortKey = 'stern' | 'packort' | 'phase' | 'faellig' | 'bestand' | 'letzte_zaehlung' | 'geaendert'
type Sortierung = { key: SortKey; richtung: 'asc' | 'desc' }
const STANDARD_SORT: Sortierung = { key: 'stern', richtung: 'asc' }
type JournalFilter = { worker_id: string; phase: string; packort: string; q: string; von: string; bis: string; fall_id: string; fall_sku: string }
const LEER_J: JournalFilter = { worker_id: '', phase: '', packort: '', q: '', von: '', bis: '', fall_id: '', fall_sku: '' }

const SELECT = 'h-9 rounded-md border border-line bg-panel px-2 text-sm focus-visible:outline-2 focus-visible:outline-primary'

function Auswahl({ label, value, onChange, children }: { label: string; value: string; onChange: (v: string) => void; children: ReactNode }) {
  return <select aria-label={label} value={value} onChange={e => onChange(e.target.value)} className={SELECT}>{children}</select>
}
function MitarbeiterAuswahl({ value, liste, onChange }: { value: string; liste: NullmengenMitarbeiter[]; onChange: (v: string) => void }) {
  const mitId = liste.filter((m): m is NullmengenMitarbeiter & { worker_id: number } => m.worker_id !== null)
  return (
    <Auswahl label="Mitarbeiter" value={value} onChange={onChange}>
      <option value="">Mitarbeiter: alle</option>
      {/* Gewählter Wert bleibt wählbar, auch wenn er im Zeitraum nicht mehr vorkommt. */}
      {value && !mitId.some(m => String(m.worker_id) === value) && <option value={value}>Mitarbeiter #{value}</option>}
      {mitId.map(m => <option key={m.worker_id} value={String(m.worker_id)}>{m.worker_name ?? `#${m.worker_id}`}</option>)}
    </Auswahl>
  )
}
function Zeitraum({ label, von, bis, onVon, onBis }: { label: string; von: string; bis: string; onVon: (v: string) => void; onBis: (v: string) => void }) {
  return (
    <span className="flex items-center gap-1 text-sm">
      <span className="text-ink-2">{label}</span>
      <Input aria-label={`${label} von`} className="w-36" type="date" value={von} max={bis || undefined} onChange={e => onVon(e.target.value)} />
      <span className="text-ink-2">–</span>
      <Input aria-label={`${label} bis`} className="w-36" type="date" value={bis} min={von || undefined} onChange={e => onBis(e.target.value)} />
    </span>
  )
}

function Kachel({ label, wert, sub, ton = '', aktiv, onClick }: { label: string; wert: number; sub?: string; ton?: string; aktiv: boolean; onClick: () => void }) {
  return (
    <button type="button" aria-pressed={aktiv} onClick={onClick}
      className={`rounded-lg border bg-panel p-2 text-left hover:bg-panel-2 focus-visible:outline-2 focus-visible:outline-primary ${aktiv ? 'border-primary ring-1 ring-primary' : 'border-line'}`}>
      <span className="block text-xs text-ink-2">{label}</span>
      <span className={`block text-2xl font-semibold tabular-nums ${ton}`}>{wert}</span>
      {sub && <span className="block text-xs text-ink-2">{sub}</span>}
    </button>
  )
}

function StatistikKopf({ s, tage, f, setFilter }: { s: NullmengenStatistik; tage: number; f: FallFilter; setFilter: (p: Partial<FallFilter>) => void }) {
  const status = (st: Status) => ({ aktiv: f.status === st, onClick: () => setFilter({ status: f.status === st ? '' : st }) })
  const tagAktiv = (tag: string) => f.faellig_arbeitstag === tag
  return (
    <>
      <section aria-label="Statistik" className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-9">
        <Kachel label="Offen" wert={s.offen} sub={`${s.ohne_termin} ohne Termin`} aktiv={f.status === ''} onClick={() => setFilter({ status: '' })} />
        <Kachel label="Heute zu zählen" wert={s.faellig} ton="text-amber-700" {...status('faellig')} />
        <Kachel label="Über Frist" wert={s.ueberfaellig} ton="text-red-700" {...status('ueberfaellig')} />
        <Kachel label="Nicht fällig" wert={s.nicht_faellig} sub={`${s.offen_gezaehlt} schon gezählt`} {...status('nicht_faellig')} />
        <Kachel label="Heute gezählt" wert={s.heute_gezaehlt.faelle} sub={zaehlungenText(s.heute_gezaehlt.zaehlungen)} ton="text-emerald-700" {...status('heute_gezaehlt')} />
        <Kachel label="Nie gezählt" wert={s.offen_nie_gezaehlt} {...status('nie_gezaehlt')} />
        <Kachel label="Klärfälle" wert={s.klaerfall} ton={s.klaerfall ? 'text-red-700' : ''} {...status('klaerfall')} />
        <Kachel label="GELÖSCHT in DR" wert={s.geloescht} ton={s.geloescht ? 'text-red-700' : ''} {...status('geloescht')} />
        <div className={`rounded-lg border bg-panel p-2 text-xs ${f.status === 'geschlossen' ? 'border-primary ring-1 ring-primary' : 'border-line'}`}>
          <button type="button" aria-pressed={f.status === 'geschlossen'} className="w-full text-left focus-visible:outline-2 focus-visible:outline-primary"
            onClick={() => setFilter({ status: f.status === 'geschlossen' ? '' : 'geschlossen' })}>
            <span className="block text-ink-2">{`Geschlossen (${tage} Tage)`}</span>
            <span className="block text-2xl font-semibold tabular-nums">{s.geschlossen.gesamt}</span>
          </button>
          <ul className="text-ink-2">
            <li>{`Aufgefüllt: ${s.geschlossen.aufgefuellt}`}</li>
            <li>{`Nachlauf fertig: ${s.geschlossen.nachlauf_fertig}`}</li>
            <li>{`Ausgeschlossen (gel\u00f6scht/unbewegt): ${s.geschlossen.ausgeschlossen}`}</li>
            <li>{`Großlager belegt: ${s.geschlossen.grosslager_belegt}`}</li>
          </ul>
        </div>
      </section>
      <section aria-label="Nächste Fälligkeiten" className="flex flex-wrap items-stretch gap-2 text-sm">
        <span className="self-center text-xs text-ink-2">Fällig in den nächsten Arbeitstagen:</span>
        {s.naechste_tage.map(t => (
          <button key={t.tag} type="button" aria-pressed={tagAktiv(t.tag)} title="Fälle dieses Tages zeigen"
            onClick={() => setFilter(tagAktiv(t.tag) ? { faellig_arbeitstag: '' }
              : { status: '', faellig_von: '', faellig_bis: '', faellig_arbeitstag: t.tag })}
            className={`rounded-md border px-2 py-1 text-left hover:bg-panel-2 focus-visible:outline-2 focus-visible:outline-primary ${tagAktiv(t.tag) ? 'border-primary bg-panel-2' : 'border-line bg-panel'}`}>
            <span className="block text-xs text-ink-2">{tagKurz(t.tag)}</span>
            <span className={`block font-semibold tabular-nums ${t.anzahl ? '' : 'text-ink-2'}`}>{t.anzahl}</span>
          </button>
        ))}
        <span className="self-center text-xs text-ink-2">{`später ${s.spaeter}`}</span>
        <span className="self-center text-xs text-ink-2">
          {s.naechste_faelligkeit ? `· nächste Fälligkeit ${datumZeit(s.naechste_faelligkeit)}` : '· keine weitere Fälligkeit geplant'}
        </span>
      </section>
    </>
  )
}

function Verteilung({ s, f, setFilter }: { s: NullmengenStatistik; f: FallFilter; setFilter: (p: Partial<FallFilter>) => void }) {
  const knopf = (aktiv: boolean) => `flex w-full items-baseline justify-between gap-2 rounded px-1 text-left hover:bg-panel-2 focus-visible:outline-2 focus-visible:outline-primary ${aktiv ? 'font-semibold text-primary' : ''}`
  return (
    <section aria-label="Verteilung" className="grid gap-3 rounded-lg border border-line bg-panel p-3 text-sm sm:grid-cols-3">
      <div>
        <h2 className="mb-1 text-xs uppercase text-ink-2">Offen nach Phase</h2>
        <ul>{s.phasen.map(p => (
          <li key={`${p.phase}-${p.runde}`}>
            <button type="button" className={knopf(f.phase === p.phase)} onClick={() => setFilter({ phase: f.phase === p.phase ? '' : p.phase })}>
              <span>{phaseKurz(p.phase, p.runde)}</span><span className="tabular-nums">{p.anzahl}</span>
            </button>
          </li>))}
        </ul>
      </div>
      <div>
        <h2 className="mb-1 text-xs uppercase text-ink-2">Offen nach Packort</h2>
        <ul>{s.packorte.map(p => (
          <li key={p.packort}>
            <button type="button" className={knopf(f.packort === p.packort)} onClick={() => setFilter({ packort: f.packort === p.packort ? '' : p.packort })}>
              <span className={p.packort === 'kein' ? 'text-red-700' : ''}>{PACKORT_TEXT[p.packort]}</span><span className="tabular-nums">{p.anzahl}</span>
            </button>
          </li>))}
        </ul>
      </div>
      <div>
        <h2 className="mb-1 text-xs uppercase text-ink-2">Wer hat gezählt ({s.zaehlungen} Zählungen, {s.faelle_gezaehlt} Fälle)</h2>
        {s.mitarbeiter.length === 0 ? <p className="text-ink-2">Noch keine Zählung im Zeitraum.</p> : (
          <ul>{s.mitarbeiter.map(m => {
            const id = m.worker_id === null ? '' : String(m.worker_id)
            return (
              <li key={id || m.worker_name || 'ohne'}>
                <button type="button" disabled={!id} className={knopf(id !== '' && f.worker_id === id)}
                  onClick={() => setFilter({ worker_id: f.worker_id === id ? '' : id })}>
                  <span>{m.worker_name ?? 'ohne Namen'}</span>
                  <span className="text-xs text-ink-2 tabular-nums">{`${zaehlungenText(m.anzahl)} · heute ${m.heute} · zuletzt ${datum(m.letzte_at)}`}</span>
                </button>
              </li>
            )
          })}</ul>
        )}
      </div>
    </section>
  )
}

function Zaehlstand({ z }: { z: NullmengenZaehlstand }) {
  const s = ZAEHLSTAND[z]
  return <span title={s.titel} className={`inline-block rounded border px-1.5 py-0.5 text-xs font-medium ${s.ton}`}>{s.text}</span>
}

function Zeile({ f, onVerlauf }: { f: NullmengenZeile; onVerlauf: () => void }) {
  const abw = drAbweichung(f)
  const lz = f.letzte_zaehlung
  const rand = f.phase === 'geschlossen' ? 'border-l-slate-300' : f.frist_ueberschritten ? 'border-l-red-600' : f.zaehlbar ? 'border-l-amber-500' : 'border-l-slate-300'
  return (
    <tr className={`border-b border-line border-l-4 ${rand}`} data-testid={`nullmengen-fall-${f.id}`}>
      <td className="p-2 align-top">
        <div className="font-medium"><GrundartikelLink code={f.merchant_sku} /></div>
        <div className="text-xs text-ink-2 break-words">{f.name ?? '—'}</div>
        {f.dr_sku && <div className="text-xs text-ink-2 break-all" title={f.ordner ?? undefined}>{f.dr_sku}</div>}
        {f.geloescht && (
          <span title={`In DreamRobot als gelöscht markiert${f.ordner ? ` (${f.ordner})` : ''}`}
            className="mt-1 inline-block rounded border border-red-600 bg-red-50 px-1.5 py-0.5 text-xs font-semibold text-red-800">GELÖSCHT in DR</span>
        )}
      </td>
      <td className="p-2 align-top">
        <span className={f.packort === 'kein' ? 'font-semibold text-red-700' : ''}>{PACKORT_TEXT[f.packort]}</span>
        {f.packplatz && <div className="text-xs text-ink-2">{f.packplatz}</div>}
        {f.klaerfall === 'packort_doppelt' && <div className="text-xs font-semibold text-red-700">Klärfall: Packstation und Packplatz</div>}
      </td>
      <td className="p-2 align-top">
        <div>{f.phase === 'geschlossen' ? SCHLIESSGRUND[f.schliess_grund ?? ''] ?? 'Geschlossen' : phaseText(f)}</div>
        <div className="text-xs text-ink-2 tabular-nums">{`S1 ${f.s1} · S2 ${f.s2}`}</div>
      </td>
      <td className="p-2 align-top"><Zaehlstand z={f.zaehlstand} /></td>
      <td className="p-2 align-top text-xs">
        <div className="text-sm">{datum(f.faellig_ab)}</div>
        {f.frist_bis && <div className="text-ink-2">{`Frist bis ${datum(f.frist_bis)}`}</div>}
        {f.frist_ueberschritten && <div className="font-semibold text-red-700">Frist überschritten</div>}
        {!f.frist_ueberschritten && f.zaehlbar && <div className="text-amber-700">fällig</div>}
      </td>
      <td className="p-2 align-top tabular-nums">
        <div>{f.bestand ?? 'unbekannt'}</div>
        <div className="text-xs text-ink-2" title="Nur Anzeige — DreamRobot wird nicht geändert">
          <span>{`DR ${f.dr_menge ?? '—'}`}</span>{abw !== null && abw !== 0 && <span className="ml-1">{`(${abw > 0 ? '+' : ''}${abw})`}</span>}
        </div>
      </td>
      <td className="p-2 align-top text-xs text-ink-2">
        {lz ? <>
          <div className="text-sm text-ink tabular-nums">{`${lz.menge} Stk. · ${datumZeit(lz.gezaehlt_at)}`}</div>
          <div>{[lz.worker_name ?? 'ohne Namen', lz.device_label].filter(Boolean).join(' · ')}</div>
          <div>{zaehlungenText(f.anzahl_zaehlungen)}</div>
        </> : <div>noch nie gezählt</div>}
      </td>
      <td className="p-2 align-top text-xs text-ink-2">
        <div>{`geöffnet ${datum(f.geoeffnet_at)}`}</div>
        {f.geschlossen_at
          ? <div>{`geschlossen ${datum(f.geschlossen_at)} · ${SCHLIESSGRUND[f.schliess_grund ?? ''] ?? f.schliess_grund ?? '—'}`}</div>
          : <div>{`aktualisiert ${datumZeit(f.geaendert_at)}`}</div>}
      </td>
      <td className="p-2 align-top">
        <Button variant="outline" size="sm" aria-label={`Verlauf ${f.merchant_sku}`} onClick={onVerlauf}>Verlauf</Button>
      </td>
    </tr>
  )
}

function SortKopf({ label, k, sort, onSort }: { label: string; k: SortKey; sort: Sortierung; onSort: (k: SortKey) => void }) {
  const aktiv = sort.key === k
  return (
    <th className="p-2" aria-sort={aktiv ? (sort.richtung === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" onClick={() => onSort(k)} className={`uppercase hover:text-ink focus-visible:outline-2 focus-visible:outline-primary ${aktiv ? 'text-primary' : ''}`}>
        {label}{aktiv ? (sort.richtung === 'asc' ? ' ▲' : ' ▼') : ''}
      </button>
    </th>
  )
}

function FallTabelle({ faelle, sort, onSort, onVerlauf }: { faelle: NullmengenZeile[]; sort: Sortierung; onSort: (k: SortKey) => void; onVerlauf: (f: NullmengenZeile) => void }) {
  if (!faelle.length) return <p className="text-sm text-ink-2">Keine Fälle für diese Filter.</p>
  return (
    <div className="overflow-x-auto rounded-lg border border-line bg-background">
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-ink-2">
          <tr className="border-b border-line">
            <SortKopf label="Grundartikel" k="stern" sort={sort} onSort={onSort} />
            <SortKopf label="Packort" k="packort" sort={sort} onSort={onSort} />
            <SortKopf label="Phase" k="phase" sort={sort} onSort={onSort} />
            <th className="p-2 uppercase">Zählstand</th>
            <SortKopf label="Fällig ab · Frist" k="faellig" sort={sort} onSort={onSort} />
            <SortKopf label="Bestand" k="bestand" sort={sort} onSort={onSort} />
            <SortKopf label="Letzte Zählung" k="letzte_zaehlung" sort={sort} onSort={onSort} />
            <SortKopf label="Geändert" k="geaendert" sort={sort} onSort={onSort} />
            <th className="p-2"><span className="sr-only">Aktion</span></th>
          </tr>
        </thead>
        <tbody>{faelle.map(f => <Zeile key={f.id} f={f} onVerlauf={() => onVerlauf(f)} />)}</tbody>
      </table>
    </div>
  )
}

function FaelleAnsicht({ f, setFilter, sort, setSort, onVerlauf }: {
  f: FallFilter; setFilter: (p: Partial<FallFilter>) => void; sort: Sortierung; setSort: (s: Sortierung) => void; onVerlauf: (f: NullmengenZeile) => void
}) {
  const q = useDebounced(f.q)
  const params = useMemo(() => {
    const p: Record<string, string> = {}
    if (f.status) p.status = f.status
    if (f.phase) p.phase = f.phase
    if (f.packort) p.packort = f.packort
    if (f.worker_id) p.worker_id = f.worker_id
    if (q.trim()) p.q = q.trim()
    for (const k of DATUMSFELDER) { const d = fertigesDatum(f[k]); if (d) p[k] = d }
    if (f.faellig_arbeitstag) p.faellig_arbeitstag = f.faellig_arbeitstag
    if (f.tage !== LEER.tage) p.tage = f.tage
    if (sort.key !== STANDARD_SORT.key || sort.richtung !== STANDARD_SORT.richtung) {
      p.sort = sort.key
      if (sort.richtung === 'desc') p.richtung = 'desc'
    }
    return p
  }, [f, q, sort])
  const filterKey = JSON.stringify(params)
  const [seite, setSeite] = useState({ key: '', offset: 0 })
  useEffect(() => setSeite({ key: filterKey, offset: 0 }), [filterKey])
  const offset = seite.key === filterKey ? seite.offset : 0
  const query = useNullmengenUebersicht(offset ? { ...params, offset: String(offset) } : params)
  const d = query.data
  const onSort = (k: SortKey) => setSort(sort.key === k ? { key: k, richtung: sort.richtung === 'asc' ? 'desc' : 'asc' } : { key: k, richtung: 'asc' })
  const gefiltert = JSON.stringify(f) !== JSON.stringify(LEER) || sort.key !== STANDARD_SORT.key || sort.richtung !== STANDARD_SORT.richtung
  return (
    <div className="space-y-3">
      {d && <StatistikKopf s={d.statistik} tage={d.tage} f={f} setFilter={setFilter} />}
      {d && <Verteilung s={d.statistik} f={f} setFilter={setFilter} />}
      <div className="flex flex-wrap items-center gap-2">
        <Input type="search" aria-label="Suche" placeholder="Sternnr., DR-Nr. oder Name" className="w-56" value={f.q} onChange={e => setFilter({ q: e.target.value })} />
        <Auswahl label="Status" value={f.status} onChange={v => setFilter({ status: istStatus(v) ? v : '' })}>
          <option value="">Status: offen</option>
          {Object.entries(STATUS_TEXT).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
        </Auswahl>
        <Auswahl label="Phase" value={f.phase} onChange={v => setFilter({ phase: v })}>
          <option value="">Phase: alle</option>
          {Object.entries(PHASE_OPTION).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
        </Auswahl>
        <Auswahl label="Packort" value={f.packort} onChange={v => setFilter({ packort: v })}>
          <option value="">Packort: alle</option>
          {PACKORTE.map(p => <option key={p} value={p}>{PACKORT_TEXT[p]}</option>)}
        </Auswahl>
        <MitarbeiterAuswahl value={f.worker_id} liste={d?.mitarbeiter ?? []} onChange={v => setFilter({ worker_id: v })} />
        <Auswahl label="Geschlossene" value={f.tage} onChange={v => setFilter({ tage: v })}>
          {['7', '30', '90', '365'].map(t => <option key={t} value={t}>{`Geschlossene: ${t} Tage`}</option>)}
        </Auswahl>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <Zeitraum label="gezählt" von={f.gezaehlt_von} bis={f.gezaehlt_bis} onVon={v => setFilter({ gezaehlt_von: v })} onBis={v => setFilter({ gezaehlt_bis: v })} />
        <Zeitraum label="fällig" von={f.faellig_von} bis={f.faellig_bis}
          onVon={v => setFilter({ faellig_von: v, faellig_arbeitstag: '' })} onBis={v => setFilter({ faellig_bis: v, faellig_arbeitstag: '' })} />
        <Zeitraum label="aktualisiert" von={f.geaendert_von} bis={f.geaendert_bis} onVon={v => setFilter({ geaendert_von: v })} onBis={v => setFilter({ geaendert_bis: v })} />
        <Button variant="outline" size="sm" disabled={!gefiltert} onClick={() => { setFilter(LEER); setSort(STANDARD_SORT) }}>Filter zurücksetzen</Button>
        <Button variant="outline" size="sm" onClick={() => void query.refetch()} disabled={query.isFetching}>Aktualisieren</Button>
      </div>
      {query.isError && <p role="alert" className="text-sm text-red-700">{warehouseErrorMessage(query.error)}</p>}
      {query.isLoading ? <p className="text-sm text-ink-2">Lade …</p> : d && <>
        <p className="text-xs text-ink-2">
          {d.faelle.length < d.gesamt ? `${offset + 1}–${offset + d.faelle.length} von ${d.gesamt} Fällen` : `${d.gesamt} Fälle`}
          {` · Stand ${datumZeit(d.jetzt)}`}
        </p>
        <FallTabelle faelle={d.faelle} sort={sort} onSort={onSort} onVerlauf={onVerlauf} />
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={!offset || query.isFetching || query.isPlaceholderData}
            onClick={() => setSeite({ key: filterKey, offset: Math.max(0, offset - 500) })}>Vorherige Fälle</Button>
          <Button variant="outline" size="sm" disabled={offset + 500 >= d.gesamt || query.isFetching || query.isPlaceholderData}
            onClick={() => setSeite({ key: filterKey, offset: offset + 500 })}>Nächste Fälle</Button>
        </div>
      </>}
    </div>
  )
}

function VerlaufAnsicht({ j, setJ }: { j: JournalFilter; setJ: (p: Partial<JournalFilter>) => void }) {
  const q = useDebounced(j.q)
  const params = useMemo(() => {
    const p: Record<string, string> = {}
    if (j.fall_id) p.fall_id = j.fall_id
    if (j.worker_id) p.worker_id = j.worker_id
    if (j.phase) p.phase = j.phase
    if (j.packort) p.packort = j.packort
    if (q.trim()) p.q = q.trim()
    const von = fertigesDatum(j.von), bis = fertigesDatum(j.bis)
    if (von) p.von = von
    if (bis) p.bis = bis
    return p
  }, [j, q])
  const filterKey = JSON.stringify(params)
  const [seiten, setSeiten] = useState<{ key: string; cursors: { before_at: string; before_id: number }[] }>({ key: '', cursors: [] })
  useEffect(() => setSeiten({ key: filterKey, cursors: [] }), [filterKey])
  const cursors = seiten.key === filterKey ? seiten.cursors : []
  const cursor = cursors.at(-1)
  const query = useNullmengenJournal(cursor ? { ...params, before_at: cursor.before_at, before_id: String(cursor.before_id) } : params)
  const d = query.data
  return (
    <div className="space-y-3">
      {j.fall_id && (
        <p className="flex items-center gap-2 text-sm">
          <span className="font-semibold">{`Nur Fall ${j.fall_sku}`}</span>
          <Button variant="outline" size="sm" onClick={() => setJ({ fall_id: '', fall_sku: '' })}>Alle Fälle zeigen</Button>
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Input type="search" aria-label="Suche" placeholder="Sternnr., DR-Nr. oder Name" className="w-56" value={j.q} onChange={e => setJ({ q: e.target.value })} />
        <MitarbeiterAuswahl value={j.worker_id} liste={d?.mitarbeiter ?? []} onChange={v => setJ({ worker_id: v })} />
        <Auswahl label="Phase" value={j.phase} onChange={v => setJ({ phase: v })}>
          <option value="">Phase: alle</option>
          {ZAEHL_PHASEN.map(p => <option key={p} value={p}>{PHASE_OPTION[p]}</option>)}
        </Auswahl>
        <Auswahl label="Packort" value={j.packort} onChange={v => setJ({ packort: v })}>
          <option value="">Packort: alle</option>
          {PACKORTE.map(p => <option key={p} value={p}>{PACKORT_TEXT[p]}</option>)}
        </Auswahl>
        <Zeitraum label="gezählt" von={j.von} bis={j.bis} onVon={v => setJ({ von: v })} onBis={v => setJ({ bis: v })} />
        <Button variant="outline" size="sm" onClick={() => setJ(LEER_J)}>Filter zurücksetzen</Button>
      </div>
      {query.isError && <p role="alert" className="text-sm text-red-700">{warehouseErrorMessage(query.error)}</p>}
      {query.isLoading ? <p className="text-sm text-ink-2">Lade …</p> : d && <>
        <p className="text-xs text-ink-2">
          {`${d.gesamt} Zählungen seit ${datum(`${d.von}T12:00:00Z`)}, neueste zuerst`}
        </p>
        {d.eintraege.length === 0 ? <p className="text-sm text-ink-2">Keine Zählungen für diese Filter.</p> : (
          <div className="overflow-x-auto rounded-lg border border-line bg-background">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-ink-2">
                <tr className="border-b border-line">
                  <th className="p-2">Gezählt am</th><th className="p-2">Grundartikel</th><th className="p-2">Mitarbeiter</th>
                  <th className="p-2">Gerät</th><th className="p-2">Menge</th><th className="p-2">Phase → Ergebnis</th><th className="p-2">Packort · Fundort</th>
                </tr>
              </thead>
              <tbody>{d.eintraege.map(e => (
                <tr key={e.id} className="border-b border-line" data-testid={`nullmengen-zaehlung-${e.id}`}>
                  <td className="p-2 align-top tabular-nums whitespace-nowrap">{datumZeit(e.gezaehlt_at)}</td>
                  <td className="p-2 align-top">
                    <GrundartikelLink code={e.merchant_sku} className="font-medium" />
                    <div className="text-xs text-ink-2 break-words">{e.name ?? '—'}</div>
                  </td>
                  <td className="p-2 align-top">{e.worker_name ?? 'ohne Namen'}</td>
                  <td className="p-2 align-top text-ink-2">{e.device_label ?? '—'}</td>
                  <td className="p-2 align-top tabular-nums">{e.menge}</td>
                  <td className="p-2 align-top">{`${phaseKurz(e.phase, e.runde)} → ${ergebnisText(e)}`}</td>
                  <td className="p-2 align-top text-xs">
                    <div>{PACKORT_TEXT[e.packort]}</div>
                    {e.fundort && <div className="text-ink-2">{`gefunden: ${e.fundort === 'packstation' ? 'Packstation' : 'Packplatz'}`}</div>}
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={!cursor || query.isFetching || query.isPlaceholderData}
            onClick={() => setSeiten({ key: filterKey, cursors: cursors.slice(0, -1) })}>Neuere Zählungen</Button>
          <Button variant="outline" size="sm" disabled={!d.next_cursor || query.isFetching || query.isPlaceholderData}
            onClick={() => { if (d.next_cursor) setSeiten({ key: filterKey, cursors: [...cursors, d.next_cursor] }) }}>Ältere Zählungen</Button>
        </div>
      </>}
    </div>
  )
}

export default function NullmengenPage() {
  const settings = useModuleSettings()
  const [tab, setTab] = useState<'faelle' | 'verlauf'>('faelle')
  const [f, setF] = useState<FallFilter>(LEER)
  const [sort, setSort] = useState<Sortierung>(STANDARD_SORT)
  const [j, setJFilter] = useState<JournalFilter>(LEER_J)
  const setFilter = (p: Partial<FallFilter>) => setF(alt => ({ ...alt, ...p }))
  const setJ = (p: Partial<JournalFilter>) => setJFilter(alt => ({ ...alt, ...p }))
  return (
    <div className="p-4 max-w-7xl mx-auto space-y-4">
      <div>
        <h1 className="text-xl font-bold">NULL-MENGEN</h1>
        <p className="text-sm text-ink-2">Grundartikel ohne Palette im Großlager — am Packort nachzählen. Gezählt wird nur in der Scan-App;
          DreamRobot und der Artikelbestand bleiben unverändert.</p>
      </div>
      {settings.data && settings.data.nullmengen_aktiv !== true && (
        <p role="status" className="rounded-lg border border-amber-500 bg-amber-50 p-3 text-sm">
          Modul ist ausgeschaltet: keine neuen Fälle, keine Zählungen.{' '}
          <Link className="underline" to="/warehouse/mitarbeiter">Schalter und Freigaben auf der Mitarbeiter-Seite</Link>
        </p>
      )}
      <div className="flex gap-2" role="group" aria-label="Ansicht">
        <Button variant={tab === 'faelle' ? 'default' : 'outline'} size="sm" aria-pressed={tab === 'faelle'} onClick={() => setTab('faelle')}>Fälle</Button>
        <Button variant={tab === 'verlauf' ? 'default' : 'outline'} size="sm" aria-pressed={tab === 'verlauf'} onClick={() => setTab('verlauf')}>Verlauf</Button>
      </div>
      {tab === 'faelle'
        ? <FaelleAnsicht f={f} setFilter={setFilter} sort={sort} setSort={setSort}
            onVerlauf={z => { setJFilter({ ...LEER_J, fall_id: String(z.id), fall_sku: z.merchant_sku }); setTab('verlauf') }} />
        : <VerlaufAnsicht j={j} setJ={setJ} />}
    </div>
  )
}
